import {
    makeVar,
    FieldReadFunction,
    FieldFunctionOptions,
    DocumentNode,
} from '@apollo/client';

/**
 * An asynchronous version of a FieldReadFunction.
 * @typedef {AsyncReadFunction}
 */
export type AsyncFieldReadFunction<TExisting, TReadResult = TExisting> = (
    existing: Readonly<TExisting> | undefined,
    options: FieldFunctionOptions,
) => Promise<TReadResult>;

/*
    11/5/20: asyncRead is not yet confirmed to work correctly.
    See notes in: packages/resolvers/src/fieldPolicies/logEvents/readLogEvents.ts
*/

/**
 * A function that wraps an asynchronous function within a synchronous FieldReadFunction, then
 * returns the synchronous wrapper function. We will use this to implement async behavior previously
 * contained in local resolvers.
 * Based on: https://github.com/apollographql/apollo-client/issues/6852#issuecomment-687091462
 * @param asyncFunction the async function you want to wrap
 * @param query the gql query string that will be updated with cache.writeQuery, passing in the
 * results of the asyncFunction
 * @param emptyData a value to use as an empty result
 */
export function asyncRead<T>(
    asyncFunction: AsyncFieldReadFunction<T, T>,
    query: DocumentNode,
    emptyData: T,
): FieldReadFunction<T, T> {
    const readFunction: FieldReadFunction<
        Readonly<T> | undefined,
        Readonly<T>
    > = (existing, options): T => {
        if (existing !== undefined) {
            asyncFunction(existing, options).then((data: T): void => {
                if (options.storage !== null) {
                    options.storage.var(data);
                    options.cache.writeQuery({
                        query,
                        data: {
                            [options.fieldName]: data,
                        },
                        variables: options.variables,
                    });
                }
            });

            return existing;
        }

        if (options.storage !== null) {
            if (!options.storage.var) {
                // eslint-disable-next-line no-param-reassign
                options.storage.var = makeVar(undefined);

                asyncFunction(existing, options).then((data: T): void => {
                    if (options.storage !== null) {
                        options.storage.var(data);
                        options.cache.writeQuery({
                            query,
                            data: {
                                [options.fieldName]: data,
                            },
                            variables: options.variables,
                        });
                    }
                });
            }

            return options.storage.var();
        }

        return emptyData;
    };

    return readFunction;
}
